import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  applyActionCode, 
  checkActionCode, 
  confirmPasswordReset,
  verifyPasswordResetCode
} from 'firebase/auth';
import { auth } from '../firebase/config';
import { getFriendlyErrorMessage } from '../utils/friendlyError';
import {
  CheckCircle,
  AlertCircle,
  Lock
} from 'lucide-react';

// Action modes from Firebase
const ACTION_MODE = {
  RESET_PASSWORD: 'resetPassword',
  VERIFY_EMAIL: 'verifyEmail',
  RECOVER_EMAIL: 'recoverEmail',
  VERIFY_AND_CHANGE_EMAIL: 'verifyAndChangeEmail'
};

export default function AuthAction() {
  const [actionMode, setActionMode] = useState(null);
  const [processing, setProcessing] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [oobCode, setOobCode] = useState('');
  
  const location = useLocation();
  const navigate = useNavigate();
  
  // Extract oobCode and determine action type when component mounts
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const code = queryParams.get('oobCode');
    const mode = queryParams.get('mode');
    

    
    if (!code) {
      setError('Invalid action link. Please request a new one.');
      setProcessing(false);
      return;
    }
    
    setOobCode(code);
    
    // If mode is directly provided in the URL, use it (common for email verification)
    // Also handle the case where mode='action' (from old email templates)
    if (mode === 'action') {

      mode = 'verifyEmail';
    }
    

    
    // Check if mode is one of our known modes or a direct string match
    if (mode && (Object.values(ACTION_MODE).includes(mode) || 
                mode === 'verifyEmail' || 
                mode === 'resetPassword' || 
                mode === 'recoverEmail' || 
                mode === 'verifyAndChangeEmail')) {
      setActionMode(mode);
      setProcessing(false);
      handleAction(mode, code);
      return;
    }
    
    // Otherwise check the action code to determine what type of action it is
    checkActionCode(auth, code)
      .then((actionCodeInfo) => {

        // Get the email from the action code info
        setEmail(actionCodeInfo.data.email || '');
        
        // Set the action mode based on the operation
        setActionMode(actionCodeInfo.operation);
        
        // If it's not a password reset, automatically process the action
        if (actionCodeInfo.operation !== ACTION_MODE.RESET_PASSWORD) {
          handleAction(actionCodeInfo.operation, code);
        } else {
          // For password reset, just stop processing and show the form
          setProcessing(false);
        }
      })
      .catch((error) => {
        console.error('Error checking action code:', error);
        
        // If we have a mode parameter but checkActionCode failed, try to use the mode directly
        if (mode) {

          setActionMode(mode);
          handleAction(mode, code);
          return;
        }
        
        setError(getFriendlyErrorMessage(error, 'This link is invalid or has expired. Please request a new one.'));
        setProcessing(false);
      });
  }, [location]);
  
  // Handle the action based on the mode
  const handleAction = async (mode, code) => {
    try {
      setProcessing(true);

      
      // Try to apply the action code regardless of mode
      // This is a fallback approach that should work for most Firebase auth actions
      try {

        await applyActionCode(auth, code);
        setSuccess(true);
        setEmail(auth.currentUser?.email || '');
        
        // Determine redirect based on mode
        let redirectParam = 'success=true';
        
        // Set timeout for redirect
        setTimeout(() => {
          navigate(`/login?${redirectParam}`);
        }, 3000);
        
        return;
      } catch (directApplyError) {
        console.error('Direct apply action failed, falling back to switch case:', directApplyError);
        // Continue to switch case as fallback
      }
      
      // Standard mode-based handling as fallback
      switch (mode) {
        case ACTION_MODE.RESET_PASSWORD:
        case 'resetPassword':

          // For password reset, we need to verify the code and get the email
          // but NOT apply the action code yet (that happens when user submits new password)
          try {
            const email = await verifyPasswordResetCode(auth, code);
            setEmail(email);
            setProcessing(false);
            // Don't redirect - show the password reset form
          } catch (error) {
            console.error('Error verifying password reset code:', error);
            setError('This password reset link is invalid or has expired. Please request a new one.');
          }
          break;
          
        case ACTION_MODE.VERIFY_EMAIL:
        case 'verifyEmail':

          await applyActionCode(auth, code);
          setSuccess(true);
          // Redirect to login after 3 seconds
          setTimeout(() => {
            navigate('/login?verified=true');
          }, 3000);
          break;
          
        case ACTION_MODE.RECOVER_EMAIL:
        case 'recoverEmail':
          await applyActionCode(auth, code);
          setSuccess(true);
          // Redirect to login after 3 seconds
          setTimeout(() => {
            navigate('/login?recovered=true');
          }, 3000);
          break;
          
        case ACTION_MODE.VERIFY_AND_CHANGE_EMAIL:
        case 'verifyAndChangeEmail':
          await applyActionCode(auth, code);
          setSuccess(true);
          // Redirect to login after 3 seconds
          setTimeout(() => {
            navigate('/login?emailChanged=true');
          }, 3000);
          break;
          
        default:
          console.error('Unknown action type:', mode);
          setError(`Unknown action type: ${mode}`);
      }
      
      setProcessing(false);
    } catch (error) {
      console.error('Error processing action:', error);
      setError(getFriendlyErrorMessage(error, 'Failed to process your request. Please try again.'));
      setProcessing(false);
    }
  };
  
  // Handle password reset form submission
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    // Validate passwords
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    try {
      setProcessing(true);
      setError('');
      
      // Confirm password reset
      await confirmPasswordReset(auth, oobCode, password);
      
      // Show success message
      setSuccess(true);
      setProcessing(false);
      
      // Redirect to login page after 3 seconds
      setTimeout(() => {
        navigate('/login?reset=success');
      }, 3000);
    } catch (error) {
      console.error('Error resetting password:', error);
      setError('Failed to reset password. Please try again or request a new link.');
      setProcessing(false);
    }
  };
  
  // Render appropriate UI based on action mode
  const renderActionUI = () => {
    if (processing && !actionMode) {
      return (
        <div className="flex flex-col items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-gray-700">Processing your request...</p>
        </div>
      );
    }
    
    if (error) {
      return (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Go to Login
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    if (success) {
      let successMessage = '';
      let redirectMessage = 'You will be redirected to the login page shortly.';
      
      switch (actionMode) {
        case ACTION_MODE.RESET_PASSWORD:
          successMessage = 'Your password has been reset successfully.';
          break;
        case ACTION_MODE.VERIFY_EMAIL:
          successMessage = `Your email address ${email} has been verified.`;
          break;
        case ACTION_MODE.RECOVER_EMAIL:
          successMessage = 'Your email has been recovered successfully.';
          break;
        case ACTION_MODE.VERIFY_AND_CHANGE_EMAIL:
          successMessage = 'Your email has been changed successfully.';
          break;
        default:
          successMessage = 'Your request has been processed successfully.';
      }
      
      return (
        <div className="rounded-md bg-green-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <CheckCircle className="h-5 w-5 text-green-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">Success!</h3>
              <div className="mt-2 text-sm text-green-700">
                <p>{successMessage}</p>
                <p className="mt-2">{redirectMessage}</p>
              </div>
              <div className="mt-4 flex justify-center">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Go to Login
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // Password reset form
    if (actionMode === ACTION_MODE.RESET_PASSWORD) {
      return (
        <form className="space-y-6" onSubmit={handlePasswordReset}>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              New Password
            </label>
            <div className="mt-1">
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="focus:ring-blue-500 focus:border-blue-500 block w-full px-3 py-2 sm:text-sm border-gray-300 rounded-md"
                placeholder="Enter your new password"
              />
            </div>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
              Confirm Password
            </label>
            <div className="mt-1">
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="focus:ring-blue-500 focus:border-blue-500 block w-full px-3 py-2 sm:text-sm border-gray-300 rounded-md"
                placeholder="Confirm your new password"
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={processing}
              className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${processing ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {processing ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>
        </form>
      );
    }
    
    // Default processing state for other actions
    return (
      <div className="flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
        <p className="text-gray-700">Processing your request...</p>
      </div>
    );
  };
  
  // Get the title based on action mode
  const getActionTitle = () => {
    switch (actionMode) {
      case ACTION_MODE.RESET_PASSWORD:
        return 'Reset Your Password';
      case ACTION_MODE.VERIFY_EMAIL:
        return 'Email Verification';
      case ACTION_MODE.RECOVER_EMAIL:
        return 'Email Recovery';
      case ACTION_MODE.VERIFY_AND_CHANGE_EMAIL:
        return 'Email Change';
      default:
        return 'Authentication Action';
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {getActionTitle()}
        </h2>
        {email && actionMode === ACTION_MODE.RESET_PASSWORD && (
          <p className="mt-2 text-center text-sm text-gray-600">
            For account: {email}
          </p>
        )}
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {renderActionUI()}
          
          {!processing && !error && !success && actionMode !== ACTION_MODE.RESET_PASSWORD && (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          )}
          
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  Need help?
                </span>
              </div>
            </div>

            <div className="mt-6">
              <a
                href="/login"
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-blue-600 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Back to Login
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
